/**
 * @file Coordinates typed detail-route outcomes with app navigation.
 */

import {
    DETAIL_ROUTE_RESOLUTION,
    resolveDetailRoute,
} from "./detailRouteResolver.js";
import {
    navigate,
    showDetails,
} from "./navigationController.js";
import { buildAttachmentDetailPath } from "./detailRoutes.js";
import { openAttachmentRouteTarget } from "./attachmentRouteActions.js";
import { showSystemToast } from "../../shared/ui/notifications.js";

const SYSTEM_TOAST_IDS = Object.freeze({
    [DETAIL_ROUTE_RESOLUTION.DENIED]: "detail-route-denied",
    [DETAIL_ROUTE_RESOLUTION.MISSING]: "detail-route-missing",
    [DETAIL_ROUTE_RESOLUTION.INVALID]: "detail-route-invalid",
    [DETAIL_ROUTE_RESOLUTION.ERROR]: "detail-route-error",
});

function getRouteMessage(outcome) {
    if (outcome.status === DETAIL_ROUTE_RESOLUTION.DENIED) {
        if (outcome.routeTarget?.itemType === "task") {
            return "You do not have permission to access this item or it has been deleted.";
        }
        return "That detail page is not available with your current access.";
    }

    if (outcome.status === DETAIL_ROUTE_RESOLUTION.MISSING) {
        if (outcome.routeTarget?.itemType === "task") {
            return "That item is unavailable or has been deleted.";
        }
        return "That detail page no longer exists or is unavailable.";
    }

    if (outcome.status === DETAIL_ROUTE_RESOLUTION.INVALID) {
        return "This detail link is invalid.";
    }

    return "Your signed-in session is ready, but the requested destination could not be restored.";
}

function notifyRouteOutcome(outcome) {
    if (outcome.status === DETAIL_ROUTE_RESOLUTION.RESOLVED) {return;}

    showSystemToast(getRouteMessage(outcome), {
        id: SYSTEM_TOAST_IDS[outcome.status] || "detail-route-notice",
    });
}

/**
 * Resolves and restores a typed detail route through normal app navigation.
 * @param {object|string} routeTarget Typed route object or pathname.
 * @param {object} [options] Coordinator options.
 * @param {"startup"|"post_auth"|"popstate"|"manual"|"notification"}
 * [options.source]
 * Caller context.
 * @param {"push"|"replace"|"none"} [options.historyMode] Browser history mode.
 * @param {object} [options.resolverOptions] Optional resolver dependencies.
 * @returns {Promise<object>} Resolver outcome plus restoration metadata.
 */
export async function restoreDetailRoute(routeTarget, options = {}) {
    const historyMode = options.historyMode || "push";
    const shouldNotify = options.notify !== false;
    const outcome = await resolveDetailRoute(
        routeTarget,
        options.resolverOptions || {},
    );

    if (outcome.status === DETAIL_ROUTE_RESOLUTION.RESOLVED) {
        await showDetails(
            outcome.routeTarget.itemId,
            outcome.routeTarget.itemType,
            { historyMode },
        );
        if (outcome.routeTarget.attachmentId) {
            const attachmentPath = buildAttachmentDetailPath(
                outcome.routeTarget.itemType,
                outcome.routeTarget.itemId,
                outcome.routeTarget.attachmentId,
                outcome.routeTarget.versionId,
            );
            if (attachmentPath && historyMode !== "none") {
                window.history.replaceState({
                    view: "details",
                    itemId: outcome.routeTarget.itemId,
                    itemType: outcome.routeTarget.itemType,
                    attachmentId: outcome.routeTarget.attachmentId,
                    versionId: outcome.routeTarget.versionId || null,
                }, "", attachmentPath);
            }
            await openAttachmentRouteTarget(outcome.routeTarget);
        }
        return { ...outcome, restored: true, source: options.source || "manual" };
    }

    navigate("tasks", null, { historyMode });
    if (shouldNotify) {
        notifyRouteOutcome(outcome);
    }
    return {
        ...outcome,
        restored: false,
        source: options.source || "manual",
        message: getRouteMessage(outcome),
        toastShown: shouldNotify,
    };
}
